import React, { useEffect, useState } from 'react';
import { FaLinkedin, FaGithub } from 'react-icons/fa';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import avatar from '../assets/avatar.png';
import moon from '../assets/moon.png';
import sunny from '../assets/sunny.png';
import mexico from '../assets/mexico.png';
import { fetchSiteContent } from '../services/api';
import { pickSiteContent } from '../utils/localizedContent';

const Hero = ({ isDarkMode, onToggleDarkMode }) => {
  const { t, i18n } = useTranslation();
  const [siteContent, setSiteContent] = useState({});

  useEffect(() => {
    const loadSiteContent = async () => {
      const content = await fetchSiteContent();
      setSiteContent(content);
    };
    loadSiteContent();
  }, []);

  const desktopDescription = pickSiteContent(
    siteContent,
    'hero_desktop_description',
    i18n.language,
    t('hero.desktopDescriptionFallback')
  );

  const mobileDescription = pickSiteContent(
    siteContent,
    'hero_mobile_description',
    i18n.language,
    t('hero.mobileDescriptionFallback')
  );

  return (
    <section id="hero" className="hidden md:flex min-h-screen items-center justify-center px-4 sm:px-6 lg:px-8 relative pt-24 pb-4 lg:pb-32">
      <div className="max-w-7xl mx-auto w-full">
        <div className="hidden md:grid md:grid-cols-2 gap-8 items-center">
          <div className="space-y-8 animate-fadeInUp">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white uppercase tracking-wide text-left">
              Angel Salinas
            </h1>

            <h2 className="text-xl sm:text-2xl lg:text-3xl text-white uppercase tracking-wider font-medium text-left">
              {t('hero.title')}
            </h2>

            <div className="flex space-x-4">
              <a
                href="https://www.linkedin.com/in/%C3%A1ngel-salinas-25a15a22a/"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors duration-300 border ${
                  isDarkMode
                    ? 'text-white bg-gray-800/80 hover:bg-gray-700 border-gray-700'
                    : 'text-gray-800 bg-gray-200/80 hover:bg-gray-300 border-gray-300'
                }`}
              >
                <FaLinkedin className="text-xl" />
              </a>
              <a
                href="https://github.com/An6el-01"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors duration-300 border ${
                  isDarkMode
                    ? 'text-white bg-gray-800/80 hover:bg-gray-700 border-gray-700'
                    : 'text-gray-800 bg-gray-200/80 hover:bg-gray-300 border-gray-300'
                }`}
              >
                <FaGithub className="text-xl" />
              </a>
            </div>

            <p className="text-lg sm:text-xl text-white max-w-md leading-relaxed text-left">
              {desktopDescription}
            </p>

            <div className={`flex items-center space-x-3 backdrop-blur-sm border rounded-full px-6 py-3 w-fit transition-all duration-300 ${
              isDarkMode
                ? 'bg-white/10 border-white/20 hover:bg-white/20'
                : 'bg-black/10 border-black/20 hover:bg-black/20'
            }`}>
              <FaMapMarkerAlt className={`text-lg transition-colors duration-300 ${
                isDarkMode ? 'text-white/80' : 'text-gray-700'
              }`} />
              <span className={`font-medium tracking-wide transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-800'
              }`}>
                {t('hero.location')}
              </span>
            </div>
          </div>

          <div className="relative flex justify-center lg:justify-end">
            <div className="absolute -top-4 -right-4 cursor-pointer hover:scale-110 transition-transform duration-300 z-10">
              <img
                src={isDarkMode ? moon : sunny}
                alt={isDarkMode ? t('hero.moonAlt') : t('hero.sunAlt')}
                className="w-8 h-8"
                onClick={onToggleDarkMode}
              />
            </div>

            <div className="relative">
              <div className="w-80 h-80 rounded-full overflow-hidden border-4 border-gray-700 shadow-2xl">
                <img
                  src={avatar}
                  alt={t('hero.avatarAlt')}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="absolute -bottom-2 -right-2 cursor-pointer hover:scale-110 transition-transform duration-300">
                <img
                  src={mexico}
                  alt={t('hero.mexicoFlagAlt')}
                  className="w-8 h-8"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="md:hidden space-y-4 animate-fadeInUp max-w-4xl">
          <div className="flex items-start gap-4 sm:gap-6 justify-between">
            <div className="flex flex-col">
              <h1 className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-wide text-left">
                Angel Salinas
              </h1>

              <div className="flex space-x-2 pt-3">
                <a
                  href="https://www.linkedin.com/in/%C3%A1ngel-salinas-25a15a22a/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-colors duration-300 border ${
                    isDarkMode
                      ? 'text-white bg-gray-800/80 hover:bg-gray-700 border-gray-700'
                      : 'text-gray-800 bg-gray-200/80 hover:bg-gray-300 border-gray-300'
                  }`}
                >
                  <FaLinkedin className="text-base sm:text-lg" />
                </a>
                <a
                  href="https://github.com/An6el-01"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-colors duration-300 border ${
                    isDarkMode
                      ? 'text-white bg-gray-800/80 hover:bg-gray-700 border-gray-700'
                      : 'text-gray-800 bg-gray-200/80 hover:bg-gray-300 border-gray-300'
                  }`}
                >
                  <FaGithub className="text-base sm:text-lg" />
                </a>
              </div>
            </div>

            <div className="relative flex-shrink-0">
              <div className="absolute -top-2 -right-2 cursor-pointer hover:scale-110 transition-transform duration-300 z-10">
                <img
                  src={isDarkMode ? moon : sunny}
                  alt={isDarkMode ? t('hero.moonAlt') : t('hero.sunAlt')}
                  className="w-5 h-5 sm:w-6 sm:h-6"
                  onClick={onToggleDarkMode}
                />
              </div>

              <div className="relative">
                <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden border-2 border-gray-700 shadow-xl">
                  <img
                    src={avatar}
                    alt={t('hero.avatarAlt')}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="absolute -bottom-1 -right-1 cursor-pointer hover:scale-110 transition-transform duration-300">
                  <img
                    src={mexico}
                    alt={t('hero.mexicoFlagAlt')}
                    className="w-5 h-5 sm:w-6 sm:h-6"
                  />
                </div>
              </div>
            </div>
          </div>

          <p className="text-base sm:text-lg text-white max-w-3xl leading-relaxed text-left">
            {mobileDescription}
          </p>

          <div className={`flex items-center space-x-2 backdrop-blur-sm border rounded-full px-3 py-1.5 sm:px-4 sm:py-2 w-fit transition-all duration-300 text-xs sm:text-sm ${
            isDarkMode
              ? 'bg-gray-800/80 border-gray-700 hover:bg-gray-700/80'
              : 'bg-black/10 border-black/20 hover:bg-black/20'
          }`}>
            <FaMapMarkerAlt className={`text-sm sm:text-base transition-colors duration-300 ${
              isDarkMode ? 'text-white/80' : 'text-gray-700'
            }`} />
            <span className={`font-medium tracking-wide transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>
              {t('hero.location')}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
